import { todayAsUtcDay } from '../util/batch-expiry.js';
import { defineQuery } from '@rocicorp/zero';
import * as z from 'zod';
import { CatalogType, ConsumableCategory, CONSUMABLES_CATEGORIES, DiscardReason, RestVialStatus, ReturnStatus, } from '../database/schema.js';
import { EventLogResources } from '../mutators/util.js';
import { batchOrderLinkQuery, batchUsageMovementQuery } from '../mutators/utils/batch-usage.js';
import { zql } from '../schema.js';
import { applyCursor } from '../util/apply-cursor.js';
import { Status } from '../util/schemas.js';
import { applyPopulate } from './populates.js';
import { tradeNamePopulates } from './products.queries.js';
import { Cursor, Sort } from './util.js';
// this is used to check whether the trade name stock is
// approaching expiry date
export const EXPIRING_DAYS_THRESHOLD = 7;
const expiringThresholdMs = EXPIRING_DAYS_THRESHOLD * 24 * 60 * 60 * 1000;
export const SearchBatchesInput = z.object({
    search: z.string().optional(),
    isConsumable: z.boolean().optional(),
    prescribedNameIds: z.array(z.string()).optional(),
    drugCatalogItemIds: z.array(z.string()).optional(),
});
export const stockStatusList = ['active', 'low', 'depleted', 'expiring', 'expired'];
export const StockStatus = z.enum(stockStatusList);
export const ListStocksFiltersInput = z.object({
    search: z.string().optional(),
    types: z.array(z.union([CatalogType, z.literal('consumable')])).optional(),
    categories: z.array(z.union([z.uuid(), z.enum(CONSUMABLES_CATEGORIES)])).optional(),
    subcategories: z.array(z.uuidv7()).optional(),
    suppliers: z.array(z.uuidv7()).optional(),
    stockLocations: z.array(z.uuidv7()).optional(),
    status: z.array(StockStatus).optional(),
});
export const ListStocksInput = z.object({
    filters: ListStocksFiltersInput.optional(),
    limit: z.number().optional(),
    cursor: Cursor.extend({
        createdAt: z.number(),
        name: z.string(),
        earliestExpiryDate: z.number().optional(),
        totalQuantity: z.number().optional(),
    }).optional(),
    sort: Sort.extend({
        field: z.string().default('createdAt'),
    }),
});
export const ListRestVialsFiltersInput = z.object({
    ids: z.array(z.uuidv7()).optional(),
    search: z.string().optional(),
    categories: z.array(z.uuid()).optional(),
    stockLocations: z.array(z.uuidv7()).optional(),
    therapySequences: z.array(z.uuidv7()).optional(),
    status: z.array(RestVialStatus).optional(),
});
export const ListRestVialsInput = z.object({
    filters: ListRestVialsFiltersInput.optional(),
    limit: z.number().optional(),
    cursor: Cursor.extend({
        createdAt: z.number(),
    }).optional(),
    sort: Sort.extend({
        field: z.string().default('createdAt'),
    }),
});
export const ListReturnsFiltersInput = z.object({
    search: z.string().optional(),
    stockLocations: z.array(z.uuidv7()).optional(),
    status: z.array(ReturnStatus).optional(),
});
export const ListReturnsInput = z.object({
    filters: ListReturnsFiltersInput.optional(),
    limit: z.number().optional(),
    cursor: Cursor.extend({
        createdAt: z.number(),
    }).optional(),
    sort: Sort.extend({
        field: z.string().default('createdAt'),
    }),
});
export const DiscardType = z.enum(['batch', 'restVial', 'return']);
export const ListDiscardsFiltersInput = z.object({
    search: z.string().optional(),
    status: z.array(Status).optional(),
    types: z.array(DiscardType).optional(),
    reasons: z.array(DiscardReason).optional(),
    substances: z.array(z.uuidv7()).optional(),
    createdAtFrom: z.number().optional(),
    createdAtTo: z.number().optional(),
});
export const ListDiscardsInput = z.object({
    filters: ListDiscardsFiltersInput.optional(),
    limit: z.number().optional(),
    cursor: Cursor.extend({
        createdAt: z.number(),
    }).optional(),
    sort: Sort.extend({
        field: z.string().default('createdAt'),
    }),
});
export function getSequenceMedicines(medicine) {
    return medicine
        .related('activeSubstance', drug => drug
        .related('activeSubstanceDetails', activeSubstance => activeSubstance.related('doseUnit'))
        .related('medicationProfiles', profile => profile.related('stabilityRules', sr => sr.related('stabilityRule'))))
        .related('prescribedName', prescribedName => prescribedName.related('drugCatalogItem', drug => drug.related('activeSubstanceDetails')));
}
export const stocksQueries = {
    listStocks: defineQuery(ListStocksInput, ({ args }) => {
        const { filters, cursor, sort, limit } = args;
        const categories = filters?.categories ?? [];
        const subcategories = filters?.subcategories ?? [];
        const types = filters?.types ?? [];
        const suppliers = filters?.suppliers ?? [];
        const stockLocations = filters?.stockLocations ?? [];
        const status = filters?.status ?? [];
        const search = filters?.search?.trim();
        const consumableSet = new Set(CONSUMABLES_CATEGORIES);
        const now = Date.now();
        let q = zql.stocks
            .related('tradeName', tradeName => tradeName
            .related('batches', batch => batch.where('deletedAt', 'IS', null).related('stockLocation').related('supplierCompany'))
            .related('medicationProfile', profile => profile.related('drugCatalogItem', catalogItem => catalogItem.related('categories', c => c.related('drugCategory').where('deletedAt', 'IS', null)))))
            .related('consumable', c => c.related('batches', batch => batch.where('deletedAt', 'IS', null).related('stockLocation').related('supplierCompany')))
            .where('deletedAt', 'IS', null);
        if (filters) {
            if (search) {
                q = q.where(({ or, cmp, exists }) => or(cmp('name', 'ILIKE', `%${search}%`), exists('tradeName', t => t.whereExists('batches', b => b.where('lotNumber', 'ILIKE', `%${search}%`).where('deletedAt', 'IS', null))), exists('consumable', t => t.whereExists('batches', b => b.where('lotNumber', 'ILIKE', `%${search}%`).where('deletedAt', 'IS', null)))));
            }
            // 1. Filter by Type
            if (types.length > 0) {
                const hasConsumableType = types.includes('consumable');
                const drugTypes = types.filter((t) => t !== 'consumable');
                q = q.where(({ or, exists }) => or(drugTypes.length > 0
                    ? exists('tradeName', tn => tn.whereExists('medicationProfile', p => p.whereExists('drugCatalogItem', d => d.where('catalogType', 'IN', drugTypes))))
                    : undefined, hasConsumableType ? exists('consumable') : undefined));
            }
            if (categories.length > 0 || subcategories.length > 0) {
                // if it's in our enum list, it's a consumable category
                const consumableCats = categories.filter((cat) => consumableSet.has(cat));
                // everything else is treated as a drug category UUID
                const drugCatIds = [...categories.filter(cat => !consumableSet.has(cat)), ...subcategories];
                q = q.where(({ or, exists }) => or(drugCatIds.length > 0
                    ? exists('tradeName', tn => tn.whereExists('medicationProfile', profile => profile.whereExists('drugCatalogItem', drug => drug.whereExists('categories', c => c.where('drugCategoryId', 'IN', drugCatIds)))))
                    : undefined, consumableCats.length > 0 ? exists('consumable', c => c.where('category', 'IN', consumableCats)) : undefined));
            }
            if (suppliers.length > 0) {
                q = q.where(({ or, exists }) => or(exists('tradeName', tn => tn.whereExists('batches', b => b.where('supplierCompanyId', 'IN', suppliers))), exists('consumable', c => c.whereExists('batches', b => b.where('supplierCompanyId', 'IN', suppliers)))));
            }
            if (stockLocations.length > 0) {
                q = q.where(({ or, exists }) => or(exists('tradeName', tn => tn.whereExists('batches', b => b.where('stockLocationId', 'IN', stockLocations))), exists('consumable', c => c.whereExists('batches', b => b.where('stockLocationId', 'IN', stockLocations)))));
            }
            if (status.length > 0) {
                q = q.where(({ or, and, cmp }) => {
                    const statusConditions = status.map(s => {
                        switch (s) {
                            case 'active':
                                return cmp('stockStatus', '=', 'active');
                            case 'low':
                                return cmp('stockStatus', '=', 'low');
                            case 'depleted':
                                return cmp('stockStatus', '=', 'depleted');
                            case 'expiring':
                                return and(cmp('earliestExpiryDate', '>', now), cmp('earliestExpiryDate', '<=', now + expiringThresholdMs));
                            case 'expired':
                                return cmp('hasUnacknowledgedExpiredBatches', '=', true);
                            default:
                                return cmp('id', 'IS NOT', null);
                        }
                    });
                    return or(...statusConditions);
                });
            }
        }
        q = applyCursor(q, { cursor, sort, limit });
        return q;
    }),
    getStockById: defineQuery(z.string(), ({ args }) => {
        return zql.stocks
            .related('created')
            .related('tradeName', tradeName => tradeName.related('batches', batch => batch.where('deletedAt', 'IS', null).related('stockLocation').related('supplierCompany')))
            .related('consumable', consumable => consumable.related('batches', batch => batch.where('deletedAt', 'IS', null).related('stockLocation').related('supplierCompany')))
            .where('id', '=', args)
            .where('deletedAt', 'IS', null)
            .one();
    }),
    // A batch is used (CY-371) when either of these two returns a row.
    getBatchUsageMovement: defineQuery(z.string(), ({ args }) => batchUsageMovementQuery(args)),
    getBatchOrderLink: defineQuery(z.string(), ({ args }) => batchOrderLinkQuery(args)),
    getBatchById: defineQuery(z.string(), ({ args }) => {
        return zql.batches
            .related('created')
            .related('stockLocation')
            .related('supplierCompany')
            .where('id', '=', args)
            .where('deletedAt', 'IS', null)
            .one();
    }),
    getBatchesByIds: defineQuery(z.array(z.string()), ({ args }) => {
        // An empty `IN []` clause is a degenerate query that can leave the view
        // stuck in `unknown`, hanging any `useSuspenseQuery` consumer forever.
        // Return a valid always-empty query instead so it completes immediately.
        if (args.length === 0)
            return zql.batches.where('id', '=', '');
        return zql.batches
            .related('created')
            .related('stockLocation')
            .related('supplierCompany')
            .related('tradeName', tn => tn
            .related('reconstitutionSolvents', rs => rs.where('deletedAt', 'IS', null).related('solutionDrug', sd => sd.where('deletedAt', 'IS', null)))
            .related('reconstitutedConcentrationUnit')
            .related('consumables', c => c.related('consumable', cc => cc.where('deletedAt', 'IS', null)).where('deletedAt', 'IS', null))
            .related('medicationProfile', p => p.related('drugCatalogItem', d => d.related('activeSubstanceDetails', a => a.related('doseUnit'))))
            .related('strengthUnit'))
            .related('consumable', c => c.where('deletedAt', 'IS', null))
            .where('id', 'IN', args)
            .where('deletedAt', 'IS', null);
    }),
    getBatchActivitiesById: defineQuery(z.string(), ({ args }) => {
        return zql.eventLogs
            .related('actor')
            .where('resource', '=', EventLogResources.batch)
            .where('resourceId', '=', args)
            .orderBy('performedAt', 'desc');
    }),
    getBatchesActivitiesByIds: defineQuery(z.array(z.string()), ({ args }) => {
        return zql.eventLogs
            .related('actor')
            .where('resource', '=', EventLogResources.batch)
            .where('resourceId', 'IN', args)
            .orderBy('performedAt', 'desc');
    }),
    searchBatches: defineQuery(SearchBatchesInput, ({ args }) => {
        const search = args?.search?.trim() || '';
        let q = zql.batches
            .related('tradeName')
            .related('consumable')
            .where('quantity', '>', 0)
            .where('deletedAt', 'IS', null)
            // expired batches must not be picked for production; a batch is usable through its expiry day (CY-371)
            .where('expiryDate', '>=', todayAsUtcDay());
        if (search) {
            q = q.where(({ cmp, or, exists }) => or(cmp('lotNumber', 'ILIKE', `%${search}%`), exists('tradeName', tn => tn.where('name', 'ILIKE', `%${search}%`)), exists('consumable', c => c.where('name', 'ILIKE', `%${search}%`))));
        }
        if (args.isConsumable === true) {
            q = q.where('consumableId', 'IS NOT', null);
        }
        else if (args.isConsumable === false) {
            q = q.where('tradeNameId', 'IS NOT', null);
        }
        if (args.prescribedNameIds) {
            q = q.whereExists('tradeName', tn => tn.where('prescribedNameId', 'IN', args.prescribedNameIds));
        }
        if (args.drugCatalogItemIds) {
            q = q.whereExists('tradeName', tn => tn.whereExists('medicationProfile', mp => mp.where('drugCatalogItemId', 'IN', args.drugCatalogItemIds).where('deletedAt', 'IS', null)));
        }
        return q;
    }),
    listRestVials: defineQuery(ListRestVialsInput, ({ args }) => {
        const { filters, cursor, sort, limit } = args;
        const ids = filters?.ids ?? [];
        const categories = filters?.categories ?? [];
        const stockLocations = filters?.stockLocations ?? [];
        const therapySequences = filters?.therapySequences ?? [];
        const search = filters?.search?.trim();
        const status = filters?.status ?? [];
        let q = zql.restVials.where('deletedAt', 'IS', null);
        if (filters) {
            if (search) {
                q = q.whereExists('tradeName', tn => tn.where('name', 'ILIKE', `%${search}%`));
            }
            if (ids.length > 0) {
                q = q.where('id', 'IN', ids);
            }
            if (categories.length > 0) {
                q = q.where(({ exists }) => exists('tradeName', tn => tn.whereExists('medicationProfile', profile => profile.whereExists('drugCatalogItem', drug => drug.whereExists('categories', c => c.where('drugCategoryId', 'IN', categories))))));
            }
            if (stockLocations.length > 0) {
                q = q.where('stockLocationId', 'IN', stockLocations);
            }
            if (therapySequences.length > 0) {
                q = q.where('sequenceId', 'IN', therapySequences);
            }
            if (status.length > 0) {
                q = q.where('status', 'IN', status);
            }
            // else {
            //   // hide discarded items unless explicitly requested via the status filter
            //   q = q.where('status', 'NOT IN', [RestVialStatus.enum.USED, RestVialStatus.enum.DISCARDED])
            // }
        }
        q = applyCursor(q, { cursor, sort, limit });
        q = q
            .related('tradeName', tradeName => tradeName
            .related('medicationProfile', profile => profile.related('drugCatalogItem', catalogItem => catalogItem.related('categories', c => c.related('drugCategory').where('deletedAt', 'IS', null))))
            .related('strengthUnit'))
            .related('stockLocation');
        return q;
    }),
    getRestVialById: defineQuery(z.string(), ({ args }) => {
        return zql.restVials
            .related('created')
            .related('stockLocation')
            .related('tradeName', tn => {
            if (tn)
                return applyPopulate(tn, tradeNamePopulates, 'full');
            return tn;
        })
            .related('patientTherapySequence')
            .related('batch')
            .where('id', '=', args)
            .where('deletedAt', 'IS', null)
            .one();
    }),
    getRestVialActivitiesById: defineQuery(z.string(), ({ args }) => {
        return zql.eventLogs
            .related('actor')
            .where('resource', '=', EventLogResources.restVial)
            .where('resourceId', '=', args)
            .orderBy('performedAt', 'desc');
    }),
    listReturns: defineQuery(ListReturnsInput, ({ args }) => {
        const { filters, cursor, sort, limit } = args;
        const stockLocations = filters?.stockLocations ?? [];
        const search = filters?.search?.trim();
        const status = filters?.status ?? [];
        let q = zql.returns
            .related('patientTherapySequence', sequence => sequence.related('patient'))
            .related('stockLocation')
            .where('deletedAt', 'IS', null);
        if (filters) {
            if (search) {
                q = q.where('medicationName', 'ILIKE', `%${search}%`);
            }
            if (stockLocations.length > 0) {
                q = q.where('stockLocationId', 'IN', stockLocations);
            }
            if (status.length > 0) {
                q = q.where('status', 'IN', status);
            }
            // else {
            //   // hide discarded items unless explicitly requested via the status filter
            //   q = q.where('status', '!=', ReturnStatus.enum.DISCARDED)
            // }
        }
        q = applyCursor(q, { cursor, sort, limit });
        return q;
    }),
    getReturnById: defineQuery(z.string(), ({ args }) => {
        return zql.returns
            .related('created', created => created.where('deletedAt', 'IS', null))
            .related('stockLocation', stockLocation => stockLocation.where('deletedAt', 'IS', null))
            .related('patientTherapySequence', sequence => sequence
            .related('patient', patient => patient.where('deletedAt', 'IS', null))
            .related('created', created => created.where('deletedAt', 'IS', null))
            .related('applicationForm', applicationForm => applicationForm.where('deletedAt', 'IS', null))
            .related('carrierSolution', carrierSolution => carrierSolution.where('deletedAt', 'IS', null))
            .related('consumables', consumables => consumables.where('deletedAt', 'IS', null))
            .related('protocol', protocol => protocol
            .related('diagnosis', diagnosis => diagnosis.where('deletedAt', 'IS', null))
            .where('deletedAt', 'IS', null))
            .related('medicine', medicine => getSequenceMedicines(medicine).where('deletedAt', 'IS', null))
            .related('observations', obs => obs
            .related('created', creator => creator.where('deletedAt', 'IS', null).related('groups', groups => groups.where('deletedAt', 'IS', null)))
            .where('deletedAt', 'IS', null))
            .related('order', o => o
            .related('batches', link => link
            .related('batch', batch => batch
            .related('tradeName', tn => tn
            .related('stabilityRules', sr => sr
            .related('stabilityRule', rule => rule.where('deletedAt', 'IS', null))
            .where('deletedAt', 'IS', null))
            .where('deletedAt', 'IS', null))
            .where('deletedAt', 'IS', null))
            .where('deletedAt', 'IS', null))
            .where('deletedAt', 'IS', null))
            .where('deletedAt', 'IS', null))
            .where('id', '=', args)
            .where('deletedAt', 'IS', null)
            .one();
    }),
    getReturnActivitiesById: defineQuery(z.string(), ({ args }) => {
        return zql.eventLogs
            .related('actor')
            .where('resource', '=', EventLogResources.return)
            .where('resourceId', '=', args)
            .orderBy('performedAt', 'desc');
    }),
    // the return created when a nurse decides not to administer a prescription
    getReturnBySequenceId: defineQuery(z.string(), ({ args }) => {
        return zql.returns
            .related('created')
            .where('sequenceId', '=', args)
            .where('deletedAt', 'IS', null)
            .orderBy('createdAt', 'desc')
            .one();
    }),
    listDiscards: defineQuery(ListDiscardsInput, ({ args }) => {
        const { filters, cursor, sort, limit } = args;
        const search = filters?.search?.trim();
        const types = filters?.types ?? [];
        const reasons = filters?.reasons ?? [];
        const substances = filters?.substances ?? [];
        const createdAtFrom = filters?.createdAtFrom;
        const createdAtTo = filters?.createdAtTo;
        let q = zql.discards
            .related('batch', batch => batch.related('tradeName'))
            .related('restVial', vial => vial.related('tradeName'))
            .related('return')
            .related('lastLocation')
            .where('deletedAt', 'IS', null);
        if (filters) {
            if (search) {
                q = q.where(({ or, exists }) => or(exists('batch', batch => batch.whereExists('tradeName', tn => tn.where('name', 'ILIKE', `%${search}%`))), exists('restVial', vial => vial.whereExists('tradeName', tn => tn.where('name', 'ILIKE', `%${search}%`))), exists('return', ret => ret.where('medicationName', 'ILIKE', `%${search}%`))));
            }
            if (types.length > 0) {
                q = q.where(({ or, cmp }) => or(types.includes('batch') ? cmp('batchId', 'IS NOT', null) : undefined, types.includes('restVial') ? cmp('restVialId', 'IS NOT', null) : undefined, types.includes('return') ? cmp('returnId', 'IS NOT', null) : undefined));
            }
            if (reasons.length > 0) {
                q = q.where('reason', 'IN', reasons);
            }
            if (substances.length > 0) {
                q = q.where(({ or, exists }) => or(exists('restVial', vial => vial.whereExists('tradeName', tn => tn.whereExists('medicationProfile', profile => profile.whereExists('drugCatalogItem', drug => drug.whereExists('activeSubstanceDetails', a => a.where('drugCatalogItemId', 'IN', substances)))))), exists('return', ret => ret.whereExists('patientTherapySequence', seq => seq.whereExists('medicine', medicine => medicine.where('activeSubstanceId', 'IN', substances))))));
            }
            if (createdAtFrom !== undefined) {
                q = q.where('createdAt', '>=', createdAtFrom);
            }
            if (createdAtTo !== undefined) {
                q = q.where('createdAt', '<=', createdAtTo);
            }
        }
        q = applyCursor(q, { cursor, sort, limit });
        return q;
    }),
    getDiscardById: defineQuery(z.string(), ({ args }) => {
        return zql.discards
            .related('created', created => created.where('deletedAt', 'IS', null))
            .related('lastLocation', lastLocation => lastLocation.where('deletedAt', 'IS', null))
            .related('batch', batch => batch
            .related('created', created => created.where('deletedAt', 'IS', null))
            .related('stockLocation', stockLocation => stockLocation.where('deletedAt', 'IS', null))
            .related('tradeName', tn => tn
            .related('medicationProfile', profile => profile
            .related('stabilityRules', sr => sr
            .related('stabilityRule', rule => rule.where('deletedAt', 'IS', null))
            .where('deletedAt', 'IS', null))
            .where('deletedAt', 'IS', null))
            .related('stabilityRules', sr => sr
            .related('stabilityRule', rule => rule.where('deletedAt', 'IS', null))
            .where('deletedAt', 'IS', null))
            .where('deletedAt', 'IS', null))
            .where('deletedAt', 'IS', null))
            .related('restVial', vial => vial
            .related('created', created => created.where('deletedAt', 'IS', null))
            .related('stockLocation', stockLocation => stockLocation.where('deletedAt', 'IS', null))
            .related('tradeName', tn => tn
            .related('medicationProfile', profile => profile
            .related('stabilityRules', sr => sr
            .related('stabilityRule', rule => rule.where('deletedAt', 'IS', null))
            .where('deletedAt', 'IS', null))
            .where('deletedAt', 'IS', null))
            .related('stabilityRules', sr => sr
            .related('stabilityRule', rule => rule.where('deletedAt', 'IS', null))
            .where('deletedAt', 'IS', null))
            .where('deletedAt', 'IS', null))
            .related('patientTherapySequence', sequence => sequence.where('deletedAt', 'IS', null))
            .where('deletedAt', 'IS', null))
            .related('return', ret => ret
            .related('created', created => created.where('deletedAt', 'IS', null))
            .related('stockLocation', stockLocation => stockLocation.where('deletedAt', 'IS', null))
            .related('patientTherapySequence', sequence => sequence
            .related('patient', patient => patient.where('deletedAt', 'IS', null))
            .related('created', created => created.where('deletedAt', 'IS', null))
            .related('applicationForm', applicationForm => applicationForm.where('deletedAt', 'IS', null))
            .related('carrierSolution', carrierSolution => carrierSolution.where('deletedAt', 'IS', null))
            .related('consumables', consumables => consumables.where('deletedAt', 'IS', null))
            .related('protocol', protocol => protocol
            .related('diagnosis', diagnosis => diagnosis.where('deletedAt', 'IS', null))
            .where('deletedAt', 'IS', null))
            .related('medicine', medicine => getSequenceMedicines(medicine).where('deletedAt', 'IS', null))
            .related('observations', obs => obs
            .related('created', creator => creator
            .where('deletedAt', 'IS', null)
            .related('groups', groups => groups.where('deletedAt', 'IS', null)))
            .where('deletedAt', 'IS', null))
            .related('order', o => o
            .related('batches', link => link
            .related('batch', batch => batch
            .related('tradeName', tn => tn
            .related('stabilityRules', sr => sr
            .related('stabilityRule', rule => rule.where('deletedAt', 'IS', null))
            .where('deletedAt', 'IS', null))
            .where('deletedAt', 'IS', null))
            .where('deletedAt', 'IS', null))
            .where('deletedAt', 'IS', null))
            .where('deletedAt', 'IS', null))
            .where('deletedAt', 'IS', null))
            .where('deletedAt', 'IS', null))
            .where('id', '=', args)
            .where('deletedAt', 'IS', null)
            .one();
    }),
    getDiscardActivitiesById: defineQuery(z.string(), ({ args }) => {
        return zql.eventLogs
            .related('actor')
            .where('resource', '=', EventLogResources.discard)
            .where('resourceId', '=', args)
            .orderBy('performedAt', 'desc');
    }),
};
//# sourceMappingURL=stocks.queries.js.map